import styled from '@emotion/styled';
import { CustomCard, colors, Text, Tooltip } from '@innovationdepartment/proxima-ui';
import { CheckCircle } from '@mui/icons-material';
import { useEffect, useRef } from 'react';
import Tag from 'ui/Tag';
import { formatCurrency } from 'utils';
import { formatCurrencyCompact } from 'utils/formatCurrency';
import { formatNumberCompact } from 'utils/formatNumber';
import BrandProductsEmptyState from './BrandProducts.EmptyState';
import type { BrandProductsViewProps } from './types';

const BrandProductsCardsContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  grid-row-gap: 16px;
  grid-column-gap: 8px;
  justify-items: center;
`;

const LabelWrapper = styled.div`
  height: 48px; /* to match 2 lines of text */
`;

const Checkmark = styled(CheckCircle)`
  & path {
    fill: ${colors.red90};
  }
`;

const TagsWrapper = styled.div`
  display: flex;
  margin-top: 8px;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;

  & > :first-of-type {
    flex: 0 0 auto; /* Do not grow or shrink; size to content */
    margin-bottom: 0; /* Remove bottom margin if wrapping weirdness occurs */
    white-space: nowrap; /* Prevent text wrapping inside bubble */
  }
`;

const BrandProductsCards = (props: BrandProductsViewProps) => {
  const { products, loading, selectedProducts, onCardClick } = props;

  const ref = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollBy({ top: 0, behavior: 'smooth' });
    }
  }, [products]);

  if (products.length === 0) return <BrandProductsEmptyState loading={loading} />;

  return (
    <BrandProductsCardsContainer ref={ref}>
      {products.map(product => {
        const LABEL_MAX_CHARS = 44;

        const isLongLabel = product.title.length > LABEL_MAX_CHARS;
        let label = (
          <LabelWrapper>
            <Text color="neutral80" variant="h6" isTermina={false}>
              {isLongLabel ? `${product.title.slice(0, LABEL_MAX_CHARS)}...` : product.title}
            </Text>
          </LabelWrapper>
        );

        if (isLongLabel) {
          label = (
            <Tooltip title={product.title} delay={1000}>
              {label}
            </Tooltip>
          );
        }

        const taxonomy = (
          product.override?.taxonomyClassification || product.taxonomyClassification
        ).at(-1);

        const isSelected = selectedProducts.some(p => p.productId === product.productId);

        return (
          <CustomCard
            key={product.productId}
            label={label}
            onClick={onCardClick ? () => onCardClick(product) : undefined}
            bodyContent={
              <TagsWrapper>
                <Tag
                  label={`Total purchased: ${formatNumberCompact(+product.itemsPurchased)}`}
                  colors={{ bgColor: colors.green10, textColor: colors.neutral80 }}
                  variant="body3Semibold"
                />
                <Tag
                  label={`Score: ${+product.classificationScore.toFixed(3)}`}
                  colors={{ bgColor: colors.red10, textColor: colors.neutral80 }}
                  variant="body3Semibold"
                />
                <Tag
                  label={`Avg price: ${formatCurrency(+product.averagePriceUsd)}`}
                  colors={{ bgColor: colors.green10, textColor: colors.neutral80 }}
                  variant="body3Semibold"
                />
                <Tag
                  label={`Total revenue: ${formatCurrencyCompact(+product.totalRevenueUsd)}`}
                  colors={{ bgColor: colors.green10, textColor: colors.neutral80 }}
                  variant="body3Semibold"
                />
              </TagsWrapper>
            }
            headerProps={{
              icon: isSelected ? <Checkmark /> : undefined,
              heroUrl: product.imageUrl || undefined,
              pill: {
                label: taxonomy as string,
                variant: 'default'
              }
            }}
          />
        );
      })}
    </BrandProductsCardsContainer>
  );
};

export default BrandProductsCards;
